import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-sand-50 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-ocean-600 hover:text-ocean-700 mb-6"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <h1 className="text-3xl font-bold text-ocean-900 mb-6">Terms of Service</h1>
        <p className="text-gray-600 mb-6">Last updated: January 12, 2026</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing and using BabyBookin ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">2. Description of Service</h2>
            <p>
              BabyBookin is a digital baby book application that allows parents and families to create, store, 
              and share memories, photos, milestones, and stories about their children. The Service includes 
              cloud storage, collaboration features, and the ability to invite family members to view and contribute.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">3. User Accounts</h2>
            <p className="mb-2">To use BabyBookin, you must:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Create an account with accurate information</li>
              <li>Be at least 18 years old or have parental consent</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activity under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">4. User Content and Ownership</h2>
            <p className="mb-2">
              You retain all rights to the content you create and upload ("User Content"), including photos, 
              text, and other materials. By using BabyBookin, you grant us a license to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Store and display your User Content as necessary to provide the Service</li>
              <li>Share your content with family members you invite</li>
              <li>Make backup copies for service reliability</li>
            </ul>
            <p className="mt-2">
              We do NOT claim ownership of your content and will NOT use it for any purpose other than providing 
              and improving the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">5. Acceptable Use</h2>
            <p className="mb-2">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Upload content that violates any law or infringes on others' rights</li>
              <li>Upload inappropriate, offensive, or harmful content</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Attempt to hack, disrupt, or compromise the Service</li>
              <li>Use automated tools to scrape or collect data from the Service</li>
              <li>Impersonate others or create fake accounts</li>
              <li>Share your account with unauthorized users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">6. Content Guidelines</h2>
            <p>
              BabyBookin is a family-friendly service. All content must be appropriate for all ages. 
              We reserve the right to remove content that violates these guidelines or our community standards.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">7. Service Availability</h2>
            <p>
              We strive to keep BabyBookin available 24/7, but we do not guarantee uninterrupted access. 
              We may suspend or terminate the Service temporarily for maintenance, updates, or other reasons. 
              We are not liable for any downtime or service interruptions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">8. Fees and Payment</h2>
            <p>
              BabyBookin is currently provided free of charge. We reserve the right to introduce paid features 
              or subscription plans in the future. Any pricing changes will be communicated in advance, 
              and you may choose to continue or cancel your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">9. Data Backup and Loss</h2>
            <p>
              While we implement backup systems and use reliable cloud infrastructure (Google Firebase), 
              we strongly recommend you maintain your own backups of important content. We are not liable 
              for any data loss, corruption, or deletion, whether caused by technical failure, user error, 
              or other circumstances.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">10. Account Termination</h2>
            <p className="mb-2">
              You may delete your account at any time through the Settings page. We may suspend or terminate 
              your account if you violate these Terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your access to the Service will be revoked</li>
              <li>Your data will be deleted within 30 days</li>
              <li>You remain responsible for any activity that occurred under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">11. Intellectual Property</h2>
            <p>
              The BabyBookin name, logo, design, and all software are owned by us and protected by intellectual 
              property laws. You may not copy, modify, or distribute our intellectual property without permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">12. Disclaimer of Warranties</h2>
            <p>
              BabyBookin is provided "AS IS" without warranties of any kind, express or implied. We do not 
              guarantee that the Service will be error-free, secure, or always available. You use the Service 
              at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">13. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, we are not liable for any indirect, incidental, special, 
              or consequential damages arising from your use of BabyBookin, including data loss, service interruptions, 
              or unauthorized access to your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">14. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from 
              your use of the Service, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">15. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Significant changes will be communicated via email 
              or in-app notification. Your continued use after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">16. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the United States. Any disputes will be resolved in 
              accordance with applicable laws and jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ocean-800 mb-3">17. Contact Information</h2>
            <p>
              For questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> support@babybookin.com<br />
              <strong>Website:</strong> www.babybookin.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}